import { IEffortRow } from './effort.types';
import { roleLabel } from '../../utils/roleLabel';
import {
  EffortViewMode,
  getAcwrStatus,
  getDeltaStatus,
  getEffortBin,
  getMonotonyStatus,
} from './useTeamEffort';
import { MetricCellClickParams } from '../MetricHistoryModal/metricHistory.constants';
import styles from './TeamEffortTab.module.scss';

interface EffortHeatmapRowProps {
  row: IEffortRow;
  mode: EffortViewMode;
  /** Max settimanale della squadra → barra proporzionale colonna Sett. */
  maxWeekTotal: number;
  onSelect?: (athleteId: string, athleteName?: string) => void;
  /** Click sulla cella-giorno (sRPE) → apre MetricHistoryModal */
  onCellClick?: (params: MetricCellClickParams) => void;
}

const ACWR_CLASS: Record<ReturnType<typeof getAcwrStatus>, string> = {
  ok: styles.acwrOk,
  warn: styles.acwrWarn,
  alert: styles.acwrAlert,
};

const DELTA_CLASS: Record<ReturnType<typeof getDeltaStatus>, string> = {
  neutral: styles.deltaNeutral,
  warn: styles.deltaWarn,
  alert: styles.deltaAlert,
};

const BIN_CLASS = [styles.bin0, styles.bin1, styles.bin2, styles.bin3, styles.bin4];

/** Tooltip cella: evento + sRPE + RPE medio */
function cellTitle(day: IEffortRow['days'][number]): string {
  if (day.missing) return 'Dato mancante';
  const parts: string[] = [];
  if (day.eventLabel) parts.push(day.eventLabel);
  if (day.srpe != null) parts.push(`sRPE ${Math.round(day.srpe)} AU`);
  if (day.rpeAvg != null) parts.push(`RPE medio ${day.rpeAvg.toFixed(1)}`);
  return parts.join(' · ');
}

/** Riga heatmap: atleta + 7 celle giorno + Sett./Δ%/ACWR/Mono */
export function EffortHeatmapRow({ row, mode, maxWeekTotal, onSelect, onCellClick }: EffortHeatmapRowProps) {
  const fullName = [row.athlete.firstName, row.athlete.lastName].filter(Boolean).join(' ') || '—';
  const barPct = maxWeekTotal > 0 ? Math.min(100, (row.weekTotal / maxWeekTotal) * 100) : 0;

  return (
    <tr
      className={styles.row}
      onClick={() => onSelect?.(row.athlete._id, fullName)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect?.(row.athlete._id, fullName);
        }
      }}
    >
      <td className={styles.tdAthlete}>
        <span className={styles.athleteName}>{fullName}</span>
        {row.athlete.role && (
          <span className={styles.athleteRole}>{roleLabel(row.athlete.role)}</span>
        )}
      </td>

      {row.days.map((day) => {
        const value = mode === 'srpe' ? day.srpe : day.rpeAvg;
        const isMissing = day.missing || value == null;
        const cellClass = isMissing
          ? styles.missing
          : BIN_CLASS[getEffortBin(mode, value as number)];
        return (
          <td
            key={day.date}
            className={styles.tdDay}
            title={cellTitle(day)}
            onClick={
              day.missing
                ? undefined
                : (e) => {
                    // stopPropagation: la <tr> naviga al dettaglio atleta, la cella apre lo storico sRPE
                    e.stopPropagation();
                    onCellClick?.({
                      athleteId: row.athlete._id,
                      athleteName: fullName,
                      metric: 'srpe',
                      highlightDate: day.date,
                    });
                  }
            }
          >
            <div className={`${styles.cellInner} ${cellClass}`}>
              {isMissing
                ? '–'
                : mode === 'srpe'
                  ? Math.round(value as number)
                  : (value as number).toFixed(1)}
            </div>
          </td>
        );
      })}

      {/* Sett. — somma settimanale + barra proporzionale al max squadra */}
      <td className={styles.tdSummary}>
        <div className={styles.weekTotalWrap}>
          <span className={styles.weekTotalValue}>{Math.round(row.weekTotal)}</span>
          <div className={styles.weekTotalBarTrack}>
            {/* width dinamica runtime → unica eccezione inline consentita */}
            <div className={styles.weekTotalBarFill} style={{ width: `${barPct}%` }} />
          </div>
        </div>
      </td>

      {/* Δ% vs settimana precedente — nascosto se null (prev = 0) */}
      <td className={`${styles.tdSummary} ${styles.tdSummaryLight}`}>
        {row.deltaPct == null ? (
          <span className={styles.emptyDash}>–</span>
        ) : (
          <span className={`${styles.deltaValue} ${DELTA_CLASS[getDeltaStatus(row.deltaPct)]}`}>
            {row.deltaPct > 0 ? '+' : ''}
            {Math.round(row.deltaPct)}%
          </span>
        )}
      </td>

      {/* ACWR badge */}
      <td className={`${styles.tdSummary} ${styles.tdSummaryLight}`}>
        {row.acwr == null ? (
          <span className={styles.emptyDash}>–</span>
        ) : (
          <span className={`${styles.acwrBadge} ${ACWR_CLASS[getAcwrStatus(row.acwr)]}`}>
            {row.acwr.toFixed(2)}
          </span>
        )}
      </td>

      {/* Monotonia */}
      <td className={`${styles.tdSummary} ${styles.tdSummaryLight}`}>
        {row.monotony == null ? (
          <span className={styles.emptyDash}>–</span>
        ) : (
          <span
            className={`${styles.monoValue} ${
              getMonotonyStatus(row.monotony) === 'warn' ? styles.monoWarn : styles.monoNeutral
            }`}
          >
            {row.monotony.toFixed(1)}
          </span>
        )}
      </td>
    </tr>
  );
}
